import { useMutation, useQuery } from "@apollo/client";
import Button from "@/components/Button";
import Dialog from "@/components/Dialog";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import { PERSON_DETAILS_HEADER_QUERY } from "@/features/person/queries";
import {
  ACHIEVEMENT_TYPES_QUERY,
  ACHIEVEMENTS_FOR_MEMBER_QUERY,
  findAchievementTypeForMember,
  type MembershipAchievementCategoryEnum,
  type MembershipAchievementForMember,
  type MembershipAchievementType
} from "@/pages/people/[pgaId]/queries";
import {
  DELETE_ACHIEVEMENT_ATTRIBUTE_MUTATION,
  DELETE_ACHIEVEMENT_CODE_MUTATION,
  DELETE_NATIVE_ACHIEVEMENT_MUTATION
} from "@/pages/people/[pgaId]/mutations";

interface DeleteAchievementModalProps {
  achievement: MembershipAchievementForMember | null;
  category: MembershipAchievementCategoryEnum;
  onClose(): void;
  open: boolean;
  pgaId: string;
}

type DeleteTarget =
  | {
      kind: "code";
      input: {
        asofDate: string;
        code: string;
        codeType: string;
      };
    }
  | {
      kind: "attribute";
      input: {
        attributeCode: string;
        attributeType: string;
      };
    }
  | {
      kind: "native";
      input: {
        id: string;
      };
    };

function resolveDeleteTarget(
  achievement: MembershipAchievementForMember,
  achievementType: MembershipAchievementType | undefined
): { reason: string; target: null } | { reason: null; target: DeleteTarget } {
  if (!achievementType) {
    return { target: null, reason: "Achievement type could not be resolved." };
  }

  if (achievementType.native) {
    return {
      reason: null,
      target: {
        kind: "native",
        input: {
          id: achievement.id
        }
      }
    };
  }

  const code = achievement.code.code?.trim();
  if (!code) {
    return { target: null, reason: "Achievement is missing a code." };
  }

  if (achievementType.ccmsCodeType) {
    if (!achievement.recognizedAt) {
      return { target: null, reason: "Achievement is missing a recognition date." };
    }

    return {
      reason: null,
      target: {
        kind: "code",
        input: {
          code,
          codeType: achievementType.ccmsCodeType,
          asofDate: achievement.recognizedAt
        }
      }
    };
  }

  if (!achievementType.ccmsAttributeType) {
    return { target: null, reason: "This achievement type cannot be deleted." };
  }

  return {
    reason: null,
    target: {
      kind: "attribute",
      input: {
        attributeType: achievementType.ccmsAttributeType,
        attributeCode: code
      }
    }
  };
}

export default function DeleteAchievementModal({
  achievement,
  category,
  onClose,
  open,
  pgaId
}: DeleteAchievementModalProps) {
  const { data: personData } = useQuery(PERSON_DETAILS_HEADER_QUERY, {
    variables: { pgaId },
    fetchPolicy: "cache-first",
    skip: !open
  });

  const { data: achievementTypesData, loading: achievementTypesLoading } = useQuery(ACHIEVEMENT_TYPES_QUERY, {
    variables: { category },
    fetchPolicy: "cache-first",
    skip: !open
  });

  const [deleteAchievementCode, { loading: deletingCode }] = useMutation(DELETE_ACHIEVEMENT_CODE_MUTATION, {
    refetchQueries: [{ query: ACHIEVEMENTS_FOR_MEMBER_QUERY, variables: { pgaId, achievement: category } }],
    awaitRefetchQueries: true
  });

  const [deleteAchievementAttribute, { loading: deletingAttribute }] = useMutation(
    DELETE_ACHIEVEMENT_ATTRIBUTE_MUTATION,
    {
      refetchQueries: [{ query: ACHIEVEMENTS_FOR_MEMBER_QUERY, variables: { pgaId, achievement: category } }],
      awaitRefetchQueries: true
    }
  );

  const [deleteNativeAchievement, { loading: deletingNative }] = useMutation(DELETE_NATIVE_ACHIEVEMENT_MUTATION, {
    refetchQueries: [{ query: ACHIEVEMENTS_FOR_MEMBER_QUERY, variables: { pgaId, achievement: category } }],
    awaitRefetchQueries: true
  });

  const deleting = deletingCode || deletingAttribute || deletingNative;
  const achievementName = achievement?.title?.trim() || achievement?.code.title?.trim() || "achievement";
  const memberName = personData?.person?.displayName?.trim() || "this member";

  const achievementType = achievement
    ? findAchievementTypeForMember(achievementTypesData?.achievementTypes ?? [], achievement)
    : undefined;
  const deleteResolution =
    achievement && !achievementTypesLoading ? resolveDeleteTarget(achievement, achievementType) : null;
  const deleteTarget = deleteResolution?.target ?? null;
  const deleteUnavailableReason = deleteResolution?.reason ?? null;

  const handleDelete = async () => {
    if (!achievement || !deleteTarget) return;

    try {
      if (deleteTarget.kind === "code") {
        const result = await deleteAchievementCode({
          variables: {
            input: {
              pgaId,
              ...deleteTarget.input
            }
          }
        });

        if (!result.data?.deleteAchievementCode.success) {
          showErrorSnackbar(result.data?.deleteAchievementCode.message || "Unable to delete achievement.");
          return;
        }
      } else if (deleteTarget.kind === "native") {
        const result = await deleteNativeAchievement({
          variables: {
            input: {
              pgaId,
              ...deleteTarget.input
            }
          }
        });

        if (!result.data?.deleteNativeAchievement.success) {
          showErrorSnackbar(result.data?.deleteNativeAchievement.message || "Unable to delete achievement.");
          return;
        }
      } else {
        const result = await deleteAchievementAttribute({
          variables: {
            input: {
              pgaId,
              ...deleteTarget.input
            }
          }
        });

        if (!result.data?.deleteAchievementAttribute.success) {
          showErrorSnackbar(result.data?.deleteAchievementAttribute.message || "Unable to delete achievement.");
          return;
        }
      }

      showSnackbar("Achievement deleted");
      onClose();
    } catch {
      showErrorSnackbar("Unable to delete achievement.");
    }
  };

  if (!open || !achievement) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClosed={onClose}
      headline="Delete Achievement"
      actions={
        <div className="flex gap-2">
          <Button variant="text" onClick={onClose} disabled={deleting}>
            Cancel
          </Button>
          <Button
            variant="filled"
            theme="error"
            trailingIcon={false}
            icon="delete"
            onClick={handleDelete}
            disabled={deleting || achievementTypesLoading || !deleteTarget}
            testId="confirm-delete-achievement"
          >
            Delete
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <p>
          You are about to delete the achievement {achievementName} for {memberName}. This cannot be undone.
        </p>
        {deleteUnavailableReason && (
          <p className="text-body-medium text-sys-error" data-testid="delete-achievement-unavailable-reason">
            {deleteUnavailableReason}
          </p>
        )}
      </div>
    </Dialog>
  );
}
